// Helpers for the Submission History screen: pull readable facts out of the stored SOAP texts. The RAW text is always
// what staff can open and download; these only give the table row a summary. The real response shape beyond
// Reference / StatusCode / StatusDescription is not documented yet, so every leaf element is surfaced rather than a
// fixed few being guessed at.

export interface ResponseField { tag: string; value: string }

function decodeEntities(s: string): string {
  return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&')
}

/** Every leaf element of the response, in order (an escaped XML payload inside a result element is unwrapped once). */
export function parseDescartesResponse(xml: string | null | undefined): ResponseField[] {
  if (!xml) return []
  const out: ResponseField[] = []
  const scan = (text: string) => {
    for (const m of text.matchAll(/<([A-Za-z][\w.:-]*)(?:\s[^>]*)?>([^<]*)<\/\1>/g)) {
      const value = decodeEntities(m[2]).trim()
      if (!value) continue
      if (value.startsWith('<') && /<\/[A-Za-z]/.test(value)) { scan(value); continue }
      out.push({ tag: m[1].replace(/^.*:/, ''), value })
    }
  }
  scan(xml)
  return out
}

const PRIORITY = ['Reference', 'LRN', 'Key', 'StatusCode', 'StatusDescription']

/** The Descartes identifiers worth showing in the table row (Reference / LRN / Key where present). */
export function responseSummary(xml: string | null | undefined): { ids: ResponseField[]; status: string | null } {
  const fields = parseDescartesResponse(xml)
  const ids = fields.filter(f => /^(Reference|LRN|Key)$/i.test(f.tag))
  const code = fields.find(f => /^StatusCode$/i.test(f.tag))?.value
  const desc = fields.find(f => /^StatusDescription$/i.test(f.tag))?.value
  return { ids, status: code || desc ? [code, desc].filter(Boolean).join(' · ') : null }
}

export function orderedFields(xml: string | null | undefined): ResponseField[] {
  const f = parseDescartesResponse(xml)
  const rank = (t: string) => { const i = PRIORITY.findIndex(p => p.toLowerCase() === t.toLowerCase()); return i < 0 ? 99 : i }
  return [...f].sort((a, b) => rank(a.tag) - rank(b.tag))
}

/** The filename the file was sent under, recovered from the stored request envelope (older rows have no file_name). */
export function filenameFromRequestXml(xml: string | null | undefined): string | null {
  const m = (xml || '').match(/<Filename>([^<]+)<\/Filename>/)
  return m ? decodeEntities(m[1]) : null
}
